import {
  Injectable,
  NgZone,
  OnDestroy,
  inject,
  signal
} from '@angular/core';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  filter,
  interval,
  of,
  retry,
  take,
  takeUntil,
  tap,
  timeout
} from 'rxjs';
import { WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';

import { isObject, uniqId } from '../helpers';

import {
  OK_RESPONSE,
  SubscribedRoom,
  WebsocketMessage,
  WebsocketServerResponse
} from './websocket.entity';
import { WEBSOCKET_OPTIONS } from './websocket.token';

@Injectable({
  providedIn: 'root'
})
export class WebsocketClient implements OnDestroy {
  private readonly options = inject(WEBSOCKET_OPTIONS, { optional: true });
  private readonly ngZone = inject(NgZone);

  public readonly isConnected = signal<boolean>(false);
  public readonly pingMs = signal<number | undefined>(undefined);
  public readonly stream$ = new Subject<any>();

  private readonly destroy$ = new Subject<void>();

  private socket: WebSocketSubject<any> | null = null;
  private subscribedRooms: SubscribedRoom[] = [];
  private intervalSubscription: Subscription | null = null;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.closeConnection();
  }

  connect(): void {
    if (!this.options) {
      return;
    }

    const config: WebSocketSubjectConfig<any> = {
      url: this.options.url!,
      // Default binary type is `blob` for the global `WebSocket`
      binaryType: this.options.binaryType,
      serializer: this.options.serializer,
      deserializer: this.options.deserializer,
      openObserver: {
        next: () => this.onConnectionOpened()
      }
    };

    this.ngZone.runOutsideAngular(() => {
      this.socket = new WebSocketSubject(config);
      this.socket
        .pipe(
          timeout(this.options!.timeout),
          retry({
            count: this.options!.reconnectAttempts,
            delay: this.options!.reconnectInterval
          })
        )
        .subscribe({
          next: (message: any) => {
            this.ngZone.run(() => this.stream$.next(message));
          },
          error: (error: any) => {
            if (error instanceof CloseEvent) {
              this.dispatchWebSocketDisconnected();
            } else {
              console.error('WebSocket connection failed:', error);
            }
          }
        });
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.closeConnection();
      this.dispatchWebSocketDisconnected();
    }
  }

  send<T = any, K = string>(
    data: WebsocketMessage<T>,
    waitResponse = false
  ): Observable<WebsocketServerResponse<K> | null> {
    if (!this.socket) {
      if (this.options) {
        // ensure we are in browser
        console.error(`You must connect to the socket before sending any data: ${JSON.stringify(data)}`);
      }

      return EMPTY;
    }

    if (waitResponse) {
      data.id = uniqId();
    }

    const response = this.waitResponse<WebsocketServerResponse<K>>(
      (message) => isObject(message) && 'id' in message && message['id'] === data.id
    );

    this.socket.next(data);

    if (!waitResponse) {
      return of(null);
    }

    return response;
  }

  subscribeToRoom(room: string, token?: string): Observable<WebsocketServerResponse<string> | null> {
    const isSubscribed = !!this.subscribedRooms.find((sub) => sub.room === room);

    if (isSubscribed) {
      return of(null);
    }

    if (!this.isConnected()) {
      this.subscribedRooms.push({ room, token });

      return of(null);
    }

    const payload: WebsocketMessage<string> = {
      id: uniqId(),
      event: 'subscribe',
      data: room
    };

    if (token) {
      payload.token = token;
    }

    return this.send(payload, true).pipe(
      tap((response) => {
        if (response && response.data !== OK_RESPONSE) {
          return;
        }

        this.subscribedRooms.push({ room, token });
      })
    );
  }

  unsubscribeFromRoom(room: string): Observable<WebsocketServerResponse<string> | null> {
    const isSubscribed = !!this.subscribedRooms.find((sub) => sub.room === room);

    if (!isSubscribed) {
      return of(null);
    }

    const payload: WebsocketMessage<string> = {
      id: uniqId(),
      event: 'unsubscribe',
      data: room
    };

    return this.send(payload, true).pipe(
      tap((response) => {
        if (response && response.data !== OK_RESPONSE) {
          return;
        }

        this.subscribedRooms = this.subscribedRooms.filter((sub) => sub.room !== room);
      })
    );
  }

  pingServer(): void {
    if (!this.socket) {
      return;
    }

    let pingedAt: number;

    this.waitResponse<number>((message: number) => message === 0xa).subscribe(() =>
      this.updatePing(Date.now() - pingedAt)
    );

    pingedAt = Date.now();
    this.socket.next(0x9);
  }

  private onConnectionOpened(): void {
    this.intervalSubscription?.unsubscribe();
    this.ngZone.runOutsideAngular(() => {
      this.intervalSubscription = interval(this.options!.pingInterval)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.pingServer());
    });

    this.isConnected.set(true);
    this.resubscribeToRooms();
  }

  private resubscribeToRooms(): void {
    this.subscribedRooms.forEach((subscription) => {
      const payload: WebsocketMessage<string> = {
        id: uniqId(),
        event: 'subscribe',
        data: subscription.room
      };

      if (subscription.token) {
        payload.token = subscription.token;
      }

      this.send(payload);
    });
  }

  /**
   * Used in many places so it's better to move the code into function
   */
  private dispatchWebSocketDisconnected(): void {
    this.isConnected.set(false);
  }

  private updatePing(pingMs: number): void {
    this.pingMs.set(pingMs);
  }

  private closeConnection(): void {
    if (this.socket !== null) {
      this.socket.complete();
      this.intervalSubscription?.unsubscribe();
    }
  }

  private waitResponse<T = any>(predicate: (message: T) => boolean): Observable<T> {
    return this.stream$.pipe(
      filter((message: T) => predicate(message)),
      take(1),
      takeUntil(this.destroy$)
    );
  }
}
